// src/components/Billing/PlansHome.js
// @flow

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ContentfulProvider from '../../services/contentful';
import { useQuery } from 'urql';
import { log } from '../../utils/jsUtils';
import { useStore } from '../../store';

import BasicHeader from './BasicHeader';
import ContentfulPricingSection from './ContentfulPricingSection';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { useTheme } from '@mui/material/styles';
import plansHomeStyles from './Styles/PlansHome.Style';

// Selectors
const selectBillingState = (state) => state.billing || {};
const selectUserState = (state) => state.user || {};
const selectAuthState = (state) => state.auth || {};

type Props = {};

const PlansHome = (props: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Get user from Redux instead of store
  const user = useSelector(selectUserState);
  log('[PlansHome] user: ', user);
  
  // For debugging
  log('[PlansHome] user from Redux:', user);
  
  // If your old code had `store.billing`, we do:
  const billing = useSelector(selectBillingState);
  const { planId } = billing;
  log('[PlansHome] planId: ', planId);

  // Only redirect if user is logged in
  if (planId && user) {
    history.push(`/checkout?planid=${planId}`);
  }

  const [result, reexecuteQuery] = useQuery({
    query: `query ($id: String!) {
      marketingPage(id: $id) {
        slug
        browserTabTitle
        columnSectionsCollection {
          items {
            __typename
            ... on PricingSection {
              pricingTable
            }
          }
        }
      }
    }`,
    variables: {
      id: '3I8FEkk3gADqUYzOw2wmxk'
    }
  });

  const { data, fetching, error } = result;
  log('[PlansHome] data: ', data);

  const selectPlan = (planId: string) => {
    // Check if user is logged in before dispatching and redirecting
    if (!user) {
      log('[PlansHome] user is NOT logged in, redirecting to login');
      // Redirect to login first, with return_to pointing to checkout with this plan
      dispatch({
        type: 'ACTION_SET_SNACK_MSG',
        snackMessage: {
          message: 'Please log in to select a plan',
          level: 'info',
          duration: 5000
        }
      });
      history.push(`/login?return_to=/checkout?planid=${planId}`);
      return;
    }
    
    log('[PlansHome] user is logged in, proceeding to checkout');
    // User is logged in, proceed normally to checkout
    dispatch({
      type: 'ACTION_SET_BILLING_PLANID',
      planId
    });
    history.push(`/checkout?planid=${planId}`);
  };

  return (
    <Grid
      container
      direction="column"
      style={plansHomeStyles.root(theme)}
    >
      <Helmet>
        <title>Neo | Plans</title>
      </Helmet>
      <BasicHeader title="Choose a plan" />

      <Grid
        item
        xs={12}
        style={plansHomeStyles.contentCtr(theme)}
      >
        {!fetching && data
          ? data.marketingPage.columnSectionsCollection.items.map((section, idx) => {
            return (
            section.__typename === 'PricingSection'
            ? <ContentfulPricingSection 
                key={`section-${idx}`}
                pricingTable={section.pricingTable}
                section={section} 
                selectPlan={selectPlan}
              />
            : null
          );
          })
          : null}
      </Grid>
    </Grid>
  );
};

// Keep the same wrapper if needed
const PlansHomeWrapper = (props: Props) => (
  <ContentfulProvider>
    <PlansHome {...props} />
  </ContentfulProvider>
);

export default PlansHomeWrapper;